import React from 'react';
import { motion } from 'framer-motion';

/**
 * Premium UI primitives for the Home Page
 * Focus on Glassmorphism, consistent spacing, and smooth animations.
 */

interface ContainerProps {
    children: React.ReactNode;
    className?: string;
    delay?: number;
}

export const FadeInContainer: React.FC<ContainerProps> = ({ children, className = '', delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        className={className}
    >
        {children}
    </motion.div>
);

export const GlassCard: React.FC<ContainerProps & { onClick?: () => void }> = ({ children, className = '', delay = 0, onClick }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -4, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.4, delay }}
        onClick={onClick}
        className={`
            relative p-6 rounded-3xl overflow-hidden cursor-pointer
            bg-white/70 dark:bg-black/40 backdrop-blur-md
            border border-slate-200/50 dark:border-white/10
            shadow-xl shadow-slate-200/50 dark:shadow-black/50
            hover:shadow-2xl hover:shadow-indigo-500/10 dark:hover:shadow-indigo-500/10
            transition-shadow group
            ${className}
        `}
    >
        {/* Subtle accent gradient */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-3xl rounded-full group-hover:bg-indigo-500/10 transition-colors" />
        {children}
    </motion.div>
);

export const SectionTitle: React.FC<{ title: string; subtitle?: string; color?: string }> = ({ title, subtitle, color = 'indigo' }) => (
    <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
            <div className={`w-1 h-4 rounded-full bg-${color}-500 shadow-lg shadow-${color}-500/50`} />
            <h2 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 dark:text-neutral-500">
                {title}
            </h2>
        </div>
        {subtitle && (
            <p className="text-slate-600 dark:text-neutral-400 text-sm font-medium">
                {subtitle}
            </p>
        )}
    </div>
);

export const DashboardGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${className}`}>
        {children}
    </div>
);
